import { useApiCall } from "~/composables/useApiCall";
import { useUserStore } from "~/store/user";

export type HubspotEvent = "user_logged_in" | "upsell_request";

interface HubspotSyncPayload {
  data: {
    attributes: {
      event: HubspotEvent;
      product?: string;
      email?: string;
      language?: string;
    };
  };
}

/**
 * Hubspot event storage keys mapping
 */
export const HUBSPOT_EVENT_KEYS = {
  login: "loginEventSent",
  "360": "upsellEventSentForMonitor",
} as const;

/**
 * Composable for syncing user events with HubSpot
 */
export const useHubspotSync = () => {
  /**
   * Check if current session is impersonated
   */
  const isImpersonatedSession = () => {
    const userStore = useUserStore();
    return userStore.isUserImpersonated;
  };

  /**
   * Track user login event in HubSpot
   */
  const trackUserLoggedIn = async (path: string) => {
    try {
      const normalizedPath = String(path ?? "");

      // Early exit for upsell routes
      if (normalizedPath.includes("upsell")) {
        return;
      }

      // Don't track events for impersonated sessions or if already tracked
      if (shouldSkipSync(HUBSPOT_EVENT_KEYS.login)) {
        return;
      }

      const payload: HubspotSyncPayload = {
        data: {
          attributes: {
            event: "user_logged_in",
          },
        },
      };

      await useApiCall("/hubspot/sync", {
        method: "POST",
        body: payload,
      });
      markEventAsSent(HUBSPOT_EVENT_KEYS.login);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to track user login in HubSpot:", error);
      // Don't throw - this is a non-critical tracking call
    }
  };

  /**
   * Track upsell request event in HubSpot
   */
  const trackUpsellRequest = async (
    product: keyof typeof HUBSPOT_EVENT_KEYS = "360",
  ) => {
    // Don't track events for impersonated sessions or if already tracked
    if (shouldSkipSync(HUBSPOT_EVENT_KEYS[product])) {
      return;
    }

    const userEmail = localStorage.getItem("logged_in_user_email") || "";
    if (!userEmail) {
      // eslint-disable-next-line no-console
      console.warn(
        "Cannot track upsell request in HubSpot: user email not found",
      );
      return;
    }

    const userStore = useUserStore();

    try {
      const payload: HubspotSyncPayload = {
        data: {
          attributes: {
            event: "upsell_request",
            product,
            email: userEmail,
            language: userStore.getLanguage, // Include user language for better segmentation in HubSpot
          },
        },
      };

      await useApiCall(
        "/hubspot/sync",
        {
          method: "POST",
          body: payload,
        },
        undefined,
        undefined,
        true,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to track upsell request in HubSpot:", error);
      // Don't throw - this is a non-critical tracking call
    }
  };

  /**
   * Get hubspot data from localStorage
   */
  const getHubspotData = (): Record<string, any> => {
    try {
      const data = localStorage.getItem("hubspot");
      return data ? JSON.parse(data) : {};
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to parse hubspot data from localStorage:", error);
      return {};
    }
  };

  /**
   * Set hubspot data in localStorage
   */
  const setHubspotData = (data: Record<string, any>) => {
    try {
      localStorage.setItem("hubspot", JSON.stringify(data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save hubspot data to localStorage:", error);
    }
  };

  /**
   * Check if sync should be skipped based on impersonated session or localStorage flag
   */
  const shouldSkipSync = (key: string): boolean => {
    // Skip if impersonated session
    if (isImpersonatedSession()) {
      return true;
    }

    // Skip if the flag is already true in localStorage
    return isAlreadySent(key);
  };

  /**
   * Check if an event has already been sent
   */
  const isAlreadySent = (key: string): boolean => {
    const hubspot = getHubspotData();
    return hubspot[key] === true;
  };

  /**
   * Mark an event as sent in localStorage
   */
  const markEventAsSent = (key: string) => {
    const hubspot = getHubspotData();
    hubspot[key] = true;
    setHubspotData(hubspot);
  };

  return {
    trackUserLoggedIn,
    trackUpsellRequest,
    getHubspotData,
    setHubspotData,
    shouldSkipSync,
    isAlreadySent,
    markEventAsSent,
  };
};
